import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { Button, SpecraButton, SpecraIconButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ArrowUp, Brain, Library, Paperclip, Share2, Square, UploadCloud } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Markdown } from 'tiptap-markdown';
import { useAuth } from '../contexts/authContext';
import { useAttachmentUploads } from '../hooks/useAttachmentUploads';
import { apiErrorMessage } from '../lib/api/apiErrorMessage';
import { artifactClient, sharepointClient } from '../lib/connect/clients';
import { createPendingRow } from '../lib/upload';
import { cn } from '../lib/utils';
import { DocumentLibraryPicker } from './DocumentLibraryPicker';
import { FileAttachment } from './FileAttachment';
import { SharePointDocumentPicker } from './SharePointDocumentPicker';
import { VoiceInputButton } from './VoiceInputButton';
export function ComposerInput({ onSubmit, onStop, submitLabel = 'Ask Specra.AI', deferClear = false, submitting, resetKey, mode = 'default', dense = false, placeholder = 'Ask Specra.AI anything...', onRemember, rememberDisabled = false }) {
    const { user } = useAuth();
    const [isSharePointPickerOpen, setIsSharePointPickerOpen] = useState(false);
    const [isDocumentLibraryOpen, setIsDocumentLibraryOpen] = useState(false);
    const sharePointDownloads = useRef(new Map());
    const uploads = useAttachmentUploads({
        persistKey: `pendingAttachments::${user?.tenant_id ?? 'signed-out'}::${user?.id ?? 'signed-out'}::${window.location.pathname}`
    });
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const editorRootRef = useRef(null);
    const [prompt, setPrompt] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isProcessingDocs, setIsProcessingDocs] = useState(false);
    const [isComposing, setIsComposing] = useState(false);
    const isSubmittingRef = useRef(false); // Track busy state immediately
    const isBusy = (submitting ?? isSubmitting) === true || isSubmittingRef.current;
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [
                        1,
                        2,
                        3,
                        4
                    ]
                }
            }),
            Typography,
            Link.configure({
                autolink: true,
                openOnClick: false,
                defaultProtocol: 'https'
            }),
            Placeholder.configure({
                placeholder
            }),
            Markdown.configure({
                html: false
            })
        ],
        content: '',
        immediatelyRender: false,
        autofocus: false,
        editable: !isBusy,
        editorProps: {
            attributes: {
                class: cn('prose prose-sm max-w-none focus:outline-none outline-none bg-transparent overflow-y-auto prose-p:my-1', dense ? 'min-h-[2.25rem] max-h-40 text-[12.5px]' : 'min-h-[3.5rem] max-h-56 text-[13.5px]'),
                'data-testid': Testids.assistant.chatInput
            },
            handleKeyDown: (_view, event)=>{
                // Shift+Enter → new line, but if previous block is styled (heading/list/code),
                // create a fresh paragraph block; otherwise insert a soft line break.
                if (event.key === 'Enter' && event.shiftKey) {
                    event.preventDefault();
                    const isStyledBlock = editor?.isActive('heading') || editor?.isActive('bulletList') || editor?.isActive('orderedList') || editor?.isActive('codeBlock') || editor?.isActive('blockquote');
                    if (isStyledBlock) {
                        editor?.chain().focus().splitBlock().setParagraph().run();
                    } else {
                        editor?.commands.setHardBreak();
                    }
                    return true;
                }
                // Enter → send (when not composing or busy)
                if (event.key === 'Enter' && !event.shiftKey && !isComposing && !isBusy) {
                    const text = editor?.getText() ?? '';
                    if (text.trim().length === 0 && uploads.rows.length === 0) return false;
                    event.preventDefault();
                    void handleSubmit();
                    return true;
                }
                return false;
            }
        },
        onUpdate: ({ editor })=>{
            // Keep a lightweight text mirror to enable/disable submit states
            setPrompt(editor.getText());
        }
    });
    // Dictation lands at the end of whatever is already drafted, as plain
    // text: inserted as content it would be parsed as markdown, and a spoken
    // "star" or "hash" is not formatting.
    const insertDictation = (text)=>{
        if (!editor) return;
        const spacer = editor.isEmpty ? '' : ' ';
        editor.chain().focus('end').insertContent({
            type: 'text',
            text: spacer + text
        }).run();
    };
    const handleFileChange = (event)=>{
        if (event.target.files) {
            const maxFileSize = 250 * 1024 * 1024; // 250MB limit
            const validFiles = [];
            const oversizedFiles = [];
            Array.from(event.target.files).forEach((f)=>{
                if (f.size > maxFileSize) {
                    oversizedFiles.push(`${f.name} (${(f.size / (1024 * 1024)).toFixed(2)}MB)`);
                } else {
                    validFiles.push(f);
                }
            });
            if (oversizedFiles.length > 0) {
                alert(`The following files exceed the 250MB limit and cannot be uploaded:\n\n${oversizedFiles.join('\n')}`);
            }
            uploads.addFiles(validFiles, user?.tenant_id ? {
                tenantId: user.tenant_id,
                ownerUserId: user.id
            } : undefined);
            event.target.value = '';
        }
    };
    const handleDragEnter = (e)=>{
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    };
    const handleDragLeave = (e)=>{
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };
    const handleDragOver = (e)=>{
        e.preventDefault();
        e.stopPropagation();
    };
    const handleDrop = (e)=>{
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const maxFileSize = 250 * 1024 * 1024; // 250MB limit
            const validFiles = [];
            const oversizedFiles = [];
            Array.from(e.dataTransfer.files).forEach((f)=>{
                if (f.size > maxFileSize) {
                    oversizedFiles.push(`${f.name} (${(f.size / (1024 * 1024)).toFixed(2)}MB)`);
                } else {
                    validFiles.push(f);
                }
            });
            if (oversizedFiles.length > 0) {
                alert(`The following files exceed the 250MB limit and cannot be uploaded:\n\n${oversizedFiles.join('\n')}`);
            }
            uploads.addFiles(validFiles, user?.tenant_id ? {
                tenantId: user.tenant_id,
                ownerUserId: user.id
            } : undefined);
            e.dataTransfer.clearData();
        }
    };
    // Recover from a failed attachment row, addressed by its stable id — never
    // by array index, which shifts under a concurrent remove. Two failure
    // classes, two strategies (picked by `uploads.retry`):
    //   1. Upload failed before any processing job existed — re-drive
    //      uploadAttachmentViaTesoro against the cached File blob.
    //   2. Processing failed after a successful upload — call
    //      ArtifactService.RetryProcessing so reverie re-runs the same bytes.
    const handleRetry = async (id)=>{
        const source = sharePointDownloads.current.get(id);
        if (source) {
            await handleSharePointDocumentSelect(source, id);
            return;
        }
        if (!user || !user.tenant_id) {
            toast.error('You must be logged in (with a tenant) to retry an upload');
            return;
        }
        await uploads.retry(id, {
            tenantId: user.tenant_id,
            ownerUserId: user.id
        });
    };
    const openFileDialog = ()=>{
        fileInputRef.current?.click();
    };
    const handleLibraryDocumentSelect = async (document)=>{
        if (uploads.rows.some((row)=>row.attachmentId === document.artifactId)) return;
        let jobId;
        try {
            await artifactClient.getArtifactChunk({
                artifactId: document.artifactId,
                chunkKind: 'processed_markdown'
            });
        } catch (error) {
            if (!(error instanceof ConnectError) || error.code !== Code.NotFound) throw error;
            const started = await artifactClient.startArtifactProcessing({
                artifactId: document.artifactId
            });
            jobId = started.job?.jobId;
            if (!jobId) throw new Error('Document processing could not start. Try again.');
        }
        const file = new File([], document.filename, {
            type: document.mimeType
        });
        Object.defineProperty(file, 'size', {
            value: Number(document.sizeBytes)
        });
        if (uploads.rows.some((row)=>row.attachmentId === document.artifactId)) return;
        uploads.addRow(createPendingRow(file, {
            progress: 100,
            status: 'uploaded',
            attachmentId: document.artifactId,
            contentType: document.mimeType,
            jobId,
            processingStatus: jobId ? 'queued' : 'ready'
        }));
    };
    const handleSharePointDocumentSelect = async (document, existingRowId)=>{
        if (!user) {
            toast.error('You must be logged in to select SharePoint documents');
            return;
        }
        // Add placeholder file immediately so the user sees download progress.
        // Addressed by its own stable id from here on \u2014 never by array index.
        const placeholderFile = new File([], document.title, {
            type: 'application/octet-stream'
        });
        const row = createPendingRow(placeholderFile, {
            status: 'uploading',
            statusLabel: 'Downloading from SharePoint\u2026',
            sourceDownloadPending: true,
            contentType: 'application/octet-stream'
        });
        const rowId = existingRowId ?? row.id;
        sharePointDownloads.current.set(rowId, document);
        if (existingRowId) {
            uploads.updateRow(rowId, (r)=>({
                    ...r,
                    status: 'uploading',
                    progress: 0,
                    statusLabel: row.statusLabel
                }));
        } else {
            uploads.addRow(row);
        }
        try {
            // Parse the resource id. Only driveItem is reachable: every resource_id
            // enies-lobby emits from listDriveItems and searchDocuments is a
            // driveItem, and GetDriveItemContent addresses a drive. listItem is
            // rejected loudly rather than downloaded as the wrong thing — supporting
            // it needs a list_id on the RPC and a Graph path that does not exist yet.
            const [resourceType, ...pathParts] = document.resource_id.split(':');
            if (resourceType !== 'driveItem') {
                throw new Error(`Unsupported SharePoint resource type: ${resourceType}`);
            }
            const [siteId, driveId] = [
                pathParts[0]?.split('/')[0],
                pathParts[0]?.split('/')[1]
            ];
            const res = await sharepointClient.getDriveItemContent({
                siteId,
                driveId,
                itemId: document.id
            });
            let sharePointFile;
            if (res.content.length > 0) {
                // Inline bytes: the whole message arrives at once, so there is no
                // progress to report between 0 and done. The streaming read this
                // replaced only ever ran on the binary branch.
                uploads.updateRow(rowId, (r)=>({
                        ...r,
                        progress: 100
                    }));
                sharePointFile = new File([
                    res.content
                ], res.fileName || document.title, {
                    type: res.mimeType || 'application/octet-stream'
                });
            } else if (res.downloadUrl) {
                // Large files come back as a URL. This fetch is a plain one against
                // Graph, so incremental progress survives where it matters most.
                const downloadResp = await fetch(res.downloadUrl);
                if (!downloadResp.ok) throw new Error('Failed to download document from URL');
                const contentLength = Number.parseInt(downloadResp.headers.get('Content-Length') || '0', 10);
                const reader = downloadResp.body?.getReader();
                let blob;
                if (reader && contentLength > 0) {
                    let loaded = 0;
                    const chunks = [];
                    while(true){
                        const { done, value } = await reader.read();
                        if (done) break;
                        chunks.push(value);
                        loaded += value.length;
                        const pct = Math.round(loaded / contentLength * 100);
                        uploads.updateRow(rowId, (r)=>({
                                ...r,
                                progress: pct
                            }));
                    }
                    blob = new Blob(chunks);
                } else {
                    blob = await downloadResp.blob();
                }
                sharePointFile = new File([
                    blob
                ], res.fileName || document.title, {
                    type: res.mimeType || downloadResp.headers.get('Content-Type') || 'application/octet-stream'
                });
            } else {
                throw new Error('SharePoint returned no content for this document');
            }
            if (sharePointFile.size === 0) throw new Error('SharePoint returned an empty document');
            sharePointDownloads.current.delete(rowId);
            // Download complete — set to 'pending' so normal upload+processing flow handles it on submit
            uploads.updateRow(rowId, (r)=>({
                    ...r,
                    file: sharePointFile,
                    progress: 0,
                    status: 'pending',
                    sourceDownloadPending: false,
                    statusLabel: undefined,
                    contentType: sharePointFile.type
                }));
        } catch (err) {
            // Update the placeholder file to error state
            uploads.updateRow(rowId, (r)=>({
                    ...r,
                    status: 'error',
                    statusLabel: apiErrorMessage(err, 'Download failed')
                }));
        }
    };
    const handleSubmit = async ()=>{
        if (uploads.hasFailedAttachments || uploads.rows.some((row)=>row.sourceDownloadPending)) return;
        if (!prompt.trim() && uploads.rows.length === 0) {
            return;
        }
        isSubmittingRef.current = true; // Set ref immediately (synchronous)
        try {
            setIsSubmitting(true); // Also set state for consistency
            // Generate markdown using tiptap-markdown if registered; otherwise fallback to text
            let markdown = editor?.getText() ?? '';
            try {
                const storage = editor?.storage ?? {};
                const getMd = storage.markdown?.getMarkdown;
                if (typeof getMd === 'function') {
                    markdown = getMd();
                }
            } catch  {
            // ignore and use plain text
            }
            // If there are files, upload (and wait on processing) via the hook.
            // uploads.submit() only resolves once every row has reached a terminal
            // state AND that state has been flushed to a committed render — so the
            // rows are observably ready before we ever reach uploads.clear() below.
            let uploadedAttachments = [];
            if (uploads.rows.length > 0) {
                if (!user || !user.tenant_id) {
                    toast.error('You must be logged in (with a tenant) to upload files');
                    setIsSubmitting(false);
                    isSubmittingRef.current = false;
                    return;
                }
                const result = await uploads.submit({
                    tenantId: user.tenant_id,
                    ownerUserId: user.id
                }, {
                    onPollStart: ()=>setIsProcessingDocs(true)
                });
                setIsProcessingDocs(false);
                if (!result.ok) {
                    setIsSubmitting(false);
                    isSubmittingRef.current = false;
                    return;
                }
                uploadedAttachments = result.attachments;
                // SAD-741: onSubmit (e.g. PromptComposer's new-chat handler) can
                // resolve and navigate away synchronously once called — with no
                // network wait of its own — so the flushSync-committed "all ready"
                // render above can otherwise be overwritten before anything (a
                // user's eye, a test) ever gets a macrotask-boundary chance to
                // observe it. A zero-delay timeout yields past that boundary.
                await new Promise((resolve)=>setTimeout(resolve, 0));
            }
            // Make sure we have either a prompt or attachments
            if (!markdown.trim() && uploadedAttachments.length === 0) {
                alert('Please enter a message or upload a file');
                setIsProcessingDocs(false);
                return;
            }
            // Call parent's onSubmit callback (remove jobId from attachments since it's internal)
            try {
                await onSubmit({
                    prompt: markdown,
                    files: [],
                    attachments: uploadedAttachments.map(({ jobId, ...att })=>att)
                });
                if (!deferClear) {
                    // Reset immediately if not deferring
                    setPrompt('');
                    uploads.clear();
                    editor?.commands.clearContent(true);
                }
            } catch (submitError) {
                setIsProcessingDocs(false); // Reset on error
                // Show error to user - you might want to add a toast notification here
                alert(`Failed to submit: ${submitError instanceof Error ? submitError.message : 'Unknown error'}`);
                throw submitError; // Re-throw to be handled by outer catch
            }
        } catch (error) {
            setIsProcessingDocs(false); // Reset on any error
            if (error instanceof Error) {
                alert(`Error: ${error.message}`);
            }
        } finally{
            // Always reset the ref when handleSubmit completes
            isSubmittingRef.current = false;
            if (submitting === undefined) {
                setIsSubmitting(false);
            }
            setIsProcessingDocs(false); // Always reset this
        }
    };
    // If deferring clearing, watch resetKey and clear when it changes
    useEffect(()=>{
        if (!deferClear) return;
        if (resetKey === undefined) return;
        setPrompt('');
        uploads.clear();
        if (submitting === undefined) {
            setIsSubmitting(false);
            isSubmittingRef.current = false; // Reset ref
        }
        editor?.commands.clearContent(true);
    }, [
        resetKey,
        deferClear,
        submitting,
        editor,
        uploads.clear
    ]);
    // Update editor's editable state when isBusy changes
    useEffect(()=>{
        if (editor) {
            editor.setEditable(!isBusy);
        }
    }, [
        editor,
        isBusy
    ]);
    // Aggregate attachment status so the banner + submit-gate share one source of
    // truth. "Failed" covers both upload errors (status==='error') and processing
    // errors (processingStatus==='error') — either is reason enough to block
    // submission. "Pending" covers anything still in flight (uploading or
    // processing) so the banner can show a useful "n of m" while we wait.
    const { failedFileCount, pendingFileCount, hasFailedAttachments, hasPendingAttachments } = uploads;
    const hasDownloadingAttachments = uploads.rows.some((row)=>row.sourceDownloadPending);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("section", {
                "aria-label": "Prompt input dropzone",
                ref: editorRootRef,
                className: cn(// Framed composer: a single hairline box holding the input and its
                // icon row (no inner card shadow), per the delivery-redesign spec.
                // `dense` (the Assist rail) keeps its own outer frame, so no box here.
                'relative outline-none focus:outline-none', dense ? 'bg-transparent p-0' : 'rounded-lg border border-border-strong bg-background'),
                onDragEnter: handleDragEnter,
                onDragLeave: handleDragLeave,
                onDragOver: handleDragOver,
                onDrop: handleDrop,
                onCompositionStart: ()=>setIsComposing(true),
                onCompositionEnd: ()=>setIsComposing(false),
                children: [
                    isDragging && /*#__PURE__*/ _jsx("div", {
                        className: "absolute inset-0 bg-primary/10 border-2 border-dashed border-primary rounded-lg flex items-center justify-center z-10",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "text-center",
                            children: [
                                /*#__PURE__*/ _jsx(UploadCloud, {
                                    className: "w-10 h-10 text-primary mx-auto"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "mt-2 font-semibold text-primary",
                                    children: "Drop files to upload"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: cn(isBusy && 'opacity-50 pointer-events-none', !dense && 'px-3.5 pt-3 pb-1'),
                        children: /*#__PURE__*/ _jsx(EditorContent, {
                            editor: editor
                        })
                    }),
                    uploads.rows.length > 0 && (hasPendingAttachments || hasFailedAttachments) && /*#__PURE__*/ _jsx("output", {
                        "data-testid": Testids.assistant.chatAttachmentBanner,
                        "data-attachment-banner-state": hasFailedAttachments ? 'error' : 'pending',
                        className: cn('mt-3 mb-1 rounded-md px-3 py-2 text-xs flex items-center justify-between gap-3', !dense && 'mx-3.5', hasFailedAttachments ? 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-200' : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'),
                        "aria-live": "polite",
                        children: /*#__PURE__*/ _jsx("span", {
                            children: hasFailedAttachments ? `${failedFileCount} of ${uploads.rows.length} attachment${uploads.rows.length === 1 ? '' : 's'} failed. Retry or remove to send.` : `${uploads.rows.length - pendingFileCount} of ${uploads.rows.length} attachment${uploads.rows.length === 1 ? '' : 's'} ready. Processing the rest…`
                        })
                    }),
                    uploads.rows.length > 0 && /*#__PURE__*/ _jsx("div", {
                        className: cn('space-y-3 my-4', !dense && 'px-3.5'),
                        children: uploads.rows.map((pf)=>/*#__PURE__*/ _jsx(FileAttachment, {
                                file: pf.file,
                                progress: pf.progress,
                                status: pf.status,
                                statusLabel: pf.statusLabel,
                                sourceArchiveName: pf.sourceArchiveName,
                                jobId: pf.jobId,
                                processingStatus: pf.processingStatus,
                                onRemove: ()=>uploads.removeRow(pf.id),
                                onRetry: ()=>void handleRetry(pf.id),
                                onJobTerminal: (_jobId, terminal)=>{
                                    // Addressed by this row's own stable id — never by index.
                                    uploads.updateRow(pf.id, (f)=>({
                                            ...f,
                                            processingStatus: terminal === 'completed' ? 'ready' : 'error',
                                            statusLabel: terminal === 'cancelled' ? 'Processing cancelled' : f.statusLabel
                                        }));
                                },
                                disabled: isBusy,
                                useDeepProcessing: pf.useDeepProcessing,
                                onToggleDeepProcessing: ()=>{
                                    uploads.updateRow(pf.id, (f)=>({
                                            ...f,
                                            useDeepProcessing: !f.useDeepProcessing
                                        }));
                                },
                                outputFormat: pf.outputFormat,
                                onOutputFormatChange: (format)=>{
                                    uploads.updateRow(pf.id, (f)=>({
                                            ...f,
                                            outputFormat: format
                                        }));
                                }
                            }, pf.id))
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: cn('flex items-center justify-between gap-2 min-w-0 flex-wrap', dense ? 'mt-1' : 'gap-1.5 px-2.5 pt-1.5 pb-2.5'),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: cn('flex items-center min-w-0 flex-wrap', dense ? 'gap-2' : 'gap-1.5'),
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                                        label: "Upload files",
                                        title: "Upload files",
                                        size: "sm",
                                        variant: "ghost",
                                        disabled: isBusy,
                                        "data-testid": Testids.assistant.composerUploadButton,
                                        onClick: openFileDialog,
                                        children: /*#__PURE__*/ _jsx(Paperclip, {
                                            "aria-hidden": "true"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                                        label: "Add from Documents",
                                        title: "Add from Documents",
                                        size: "sm",
                                        variant: "ghost",
                                        disabled: isBusy,
                                        "data-testid": Testids.assistant.addFromDocumentsButton,
                                        onClick: ()=>setIsDocumentLibraryOpen(true),
                                        children: /*#__PURE__*/ _jsx(Library, {
                                            "aria-hidden": "true"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                                        label: "Add from SharePoint",
                                        title: "Add from SharePoint",
                                        size: "sm",
                                        variant: "ghost",
                                        disabled: isBusy,
                                        "data-testid": Testids.assistant.composerDocumentPickerButton,
                                        onClick: ()=>setIsSharePointPickerOpen(true),
                                        children: /*#__PURE__*/ _jsx(Share2, {
                                            "aria-hidden": "true"
                                        })
                                    }),
                                    mode === 'chat' && onRemember ? /*#__PURE__*/ _jsx(SpecraIconButton, {
                                        "data-testid": Testids.memory.rememberButton,
                                        disabled: rememberDisabled || isBusy,
                                        label: "Remember this",
                                        onClick: ()=>onRemember(editor?.getText() ?? ''),
                                        size: "sm",
                                        variant: "ghost",
                                        children: /*#__PURE__*/ _jsx(Brain, {
                                            "aria-hidden": "true"
                                        })
                                    }) : null,
                                    /*#__PURE__*/ _jsx(VoiceInputButton, {
                                        dense: dense,
                                        onTranscript: insertDictation,
                                        disabled: isBusy
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: cn('flex items-center', dense ? 'gap-2' : 'gap-2.5'),
                                children: [
                                    !dense && !isBusy && /*#__PURE__*/ _jsx("span", {
                                        className: "hidden text-[11px] text-muted-foreground sm:inline",
                                        children: "↵ to send"
                                    }),
                                    isBusy && onStop ? dense ? /*#__PURE__*/ _jsx(Button, {
                                        onClick: onStop,
                                        size: "icon",
                                        className: "h-[26px] w-[26px] flex-shrink-0 rounded-md bg-primary text-primary-foreground hover:bg-primary/90",
                                        title: "Stop",
                                        "aria-label": "Stop",
                                        "data-testid": Testids.assistant.stopButton,
                                        children: /*#__PURE__*/ _jsx(Square, {
                                            className: "h-3 w-3 fill-current"
                                        })
                                    }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                        className: "flex-shrink-0",
                                        "data-testid": Testids.assistant.stopButton,
                                        iconLeft: /*#__PURE__*/ _jsx(Square, {
                                            "aria-hidden": "true",
                                            className: "fill-current"
                                        }),
                                        onClick: onStop,
                                        size: "sm",
                                        children: "Stop"
                                    }) : dense ? /*#__PURE__*/ _jsx(Button, {
                                        onClick: handleSubmit,
                                        size: "icon",
                                        className: "h-[26px] w-[26px] flex-shrink-0 rounded-md bg-primary text-primary-foreground hover:bg-primary/90",
                                        disabled: isBusy || hasFailedAttachments || hasDownloadingAttachments,
                                        title: hasFailedAttachments ? 'Fix failed attachments' : submitLabel,
                                        "aria-label": submitLabel,
                                        "data-testid": Testids.assistant.sendMessageButton,
                                        children: /*#__PURE__*/ _jsx(ArrowUp, {
                                            className: "h-[13px] w-[13px]"
                                        })
                                    }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                        className: "flex-shrink-0",
                                        "data-testid": Testids.assistant.sendMessageButton,
                                        disabled: isBusy || hasFailedAttachments || hasDownloadingAttachments,
                                        onClick: handleSubmit,
                                        size: "sm",
                                        children: isProcessingDocs ? 'Processing documents…' : isBusy ? 'Sending…' : hasFailedAttachments ? 'Fix failed attachments' : submitLabel
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("input", {
                        type: "file",
                        ref: fileInputRef,
                        onChange: handleFileChange,
                        className: "hidden",
                        multiple: true
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(DocumentLibraryPicker, {
                open: isDocumentLibraryOpen,
                onOpenChange: setIsDocumentLibraryOpen,
                onSelect: handleLibraryDocumentSelect
            }),
            /*#__PURE__*/ _jsx(SharePointDocumentPicker, {
                isOpen: isSharePointPickerOpen,
                onClose: ()=>setIsSharePointPickerOpen(false),
                onSelect: handleSharePointDocumentSelect
            })
        ]
    });
}
